import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, User, Phone, Calendar, Medal, BookOpen, HandHeart, Clock, Home, Edit, Trash2, TrendingUp, Share2, QrCode, Copy, Check, Pencil, MessageCircle, BarChart3, Camera } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '../components/ui/dialog';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import Navigation from '../components/Navigation';
import { getAdminHeaders, canEdit, isFullAdmin } from '../lib/auth';
import { getSaturdayOfWeek, getWeekNumber } from '../lib/weekUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PerfilCrianca = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [score, setScore] = useState(null);
  const [settings, setSettings] = useState(null);
  const [dailyActivities, setDailyActivities] = useState([]);
  const [weeklyActivities, setWeeklyActivities] = useState([]);
  const [evolutionData, setEvolutionData] = useState([]);
  const [weeklyDetail, setWeeklyDetail] = useState(null);
  const [inviteLink, setInviteLink] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (id) {
      fetchChildData();
    }
  }, [id]);

  const fetchChildData = async () => {
    // Child is the only required data. Others are best-effort.
    try {
      const childRes = await axios.get(`${API}/children/${id}`);
      setChild(childRes.data);
    } catch (error) {
      console.error('Erro ao carregar criança:', error);
      if (error.response?.status === 404) {
        toast.error('Criança não encontrada');
      } else {
        toast.error('Erro ao carregar perfil. Tente novamente.');
      }
      navigate(-1);
      return;
    }

    // Best-effort loads — failures here don't block the page.
    try { const r = await axios.get(`${API}/scoring/child/${id}`); setScore(r.data); } catch (e) { console.warn('Score not available:', e); }
    try { const r = await axios.get(`${API}/activities/daily/${id}`); setDailyActivities(r.data); } catch (e) { console.warn('Daily activities not available:', e); }
    try { const r = await axios.get(`${API}/activities/weekly/${id}`); setWeeklyActivities(r.data); } catch (e) { console.warn('Weekly activities not available:', e); }
    try { const r = await axios.get(`${API}/settings`); setSettings(r.data); } catch (e) { console.warn('Settings not available:', e); }
    try { const r = await axios.get(`${API}/reports/evolution/${id}`); setEvolutionData(r.data.evolution || []); } catch (e) { console.warn('Evolution not available:', e); setEvolutionData([]); }
    try { const r = await axios.get(`${API}/reports/weekly-detail/${id}`); setWeeklyDetail(r.data); } catch (e) { console.warn('Weekly detail not available:', e); }
  };

  const handleDeleteDaily = async (activityId) => {
    try {
      await axios.delete(`${API}/activities/daily/${activityId}`, { headers: getAdminHeaders() });
      toast.success('Atividade removida com sucesso!');
      fetchChildData();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast.error('Erro ao remover atividade');
    }
  };

  const handleDeleteWeekly = async (activityId) => {
    try {
      await axios.delete(`${API}/activities/weekly/${activityId}`, { headers: getAdminHeaders() });
      toast.success('Atividade removida com sucesso!');
      fetchChildData();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast.error('Erro ao remover atividade');
    }
  };

  const [editDailyOpen, setEditDailyOpen] = useState(false);
  const [editWeeklyOpen, setEditWeeklyOpen] = useState(false);
  const [editDailyData, setEditDailyData] = useState(null);
  const [editWeeklyData, setEditWeeklyData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  const openEditDaily = (activity) => {
    setEditDailyData({
      id: activity.id,
      data: typeof activity.data === 'string' ? activity.data.split('T')[0] : activity.data,
      devocional: activity.devocional,
      evidencia: activity.evidencia || ''
    });
    setEditDailyOpen(true);
  };

  const handleEditDaily = async () => {
    if (!editDailyData) return;
    setEditLoading(true);
    try {
      await axios.put(`${API}/activities/daily/${editDailyData.id}`, {
        data: editDailyData.data,
        devocional: editDailyData.devocional,
        evidencia: editDailyData.evidencia
      }, { headers: getAdminHeaders() });
      toast.success('Atividade atualizada!');
      setEditDailyOpen(false);
      fetchChildData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setEditLoading(false);
    }
  };

  const openEditWeekly = (activity) => {
    setEditWeeklyData({
      id: activity.id,
      semana: typeof activity.semana === 'string' ? activity.semana.split('T')[0] : activity.semana,
      presenca: activity.presenca,
      pontualidade: activity.pontualidade,
      biblia: activity.biblia,
      visita: activity.visita,
      versiculo: activity.versiculo,
      gincana: activity.gincana || 0,
      se_vira: activity.se_vira || 0,
      observacao: activity.observacao || ''
    });
    setEditWeeklyOpen(true);
  };

  const handleEditWeekly = async () => {
    if (!editWeeklyData) return;
    setEditLoading(true);
    try {
      const { id, ...updateData } = editWeeklyData;
      await axios.put(`${API}/activities/weekly/${id}`, updateData, { headers: getAdminHeaders() });
      toast.success('Atividade atualizada!');
      setEditWeeklyOpen(false);
      fetchChildData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteChild = async () => {
    try {
      await axios.delete(`${API}/children/${id}`, { headers: getAdminHeaders() });
      toast.success('Crianca removida com sucesso!');
      navigate(-1);
    } catch (error) {
      console.error('Erro ao excluir crianca:', error);
      toast.error('Erro ao excluir crianca');
    }
  };

  const fetchInviteLink = async () => {
    try {
      const res = await axios.get(`${API}/children/${id}/invite-link`);
      const fullLink = `${window.location.origin}/app-pais/convite/${res.data.invite_token}`;
      setInviteLink(fullLink);
    } catch (error) {
      console.error('Erro ao gerar link:', error);
      toast.error('Erro ao gerar link de convite');
    }
  };

  const copyToClipboard = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const sendWhatsApp = () => {
    if (!inviteLink || !child) return;
    const celular = child.celular_pai || child.celular_mae || '';
    const cleanNumber = celular.replace(/\D/g, '');
    const fullNumber = cleanNumber.startsWith('55') ? cleanNumber : `55${cleanNumber}`;
    const msg = encodeURIComponent(
      `Olá! Segue o link de acesso ao Projeto Sementes para acompanhar as atividades de ${child.nome}:\n\n${inviteLink}\n\nCom esse link você poderá se cadastrar e acompanhar o progresso do(a) seu(sua) filho(a).`
    );
    window.open(`https://wa.me/${fullNumber}?text=${msg}`, '_blank');
  };

  const getMedalInfo = (medalha, totalPontos) => {
    const tiers = [
      { key: 'nenhuma', label: 'Semente', color: 'text-gray-400', bg: 'bg-gray-50 border-gray-200', next: 50, icon: 'seed' },
      { key: 'bronze', label: 'Bronze', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-300', next: 100, icon: 'bronze' },
      { key: 'prata', label: 'Prata', color: 'text-gray-500', bg: 'bg-gray-100 border-gray-400', next: 200, icon: 'silver' },
      { key: 'ouro', label: 'Ouro', color: 'text-yellow-500', bg: 'bg-yellow-50 border-yellow-400', next: null, icon: 'gold' },
    ];
    const current = tiers.find(t => t.key === medalha) || tiers[0];
    const currentIdx = tiers.indexOf(current);
    const prevThreshold = currentIdx > 0 ? tiers[currentIdx - 1].next : 0;
    const nextThreshold = current.next;
    let progress = 1;
    if (nextThreshold) {
      progress = Math.min((totalPontos - prevThreshold) / (nextThreshold - prevThreshold), 1);
    }
    return { ...current, progress, nextThreshold, prevThreshold };
  };

  const MedalBadge = ({ medalha, size = 'lg' }) => {
    const sizes = { sm: 'w-8 h-8 text-xs', md: 'w-12 h-12 text-sm', lg: 'w-16 h-16 text-lg' };
    const gradients = {
      ouro: 'from-yellow-300 to-yellow-500',
      prata: 'from-gray-300 to-gray-400',
      bronze: 'from-amber-400 to-amber-600',
      nenhuma: 'from-green-200 to-green-400',
    };
    return (
      <div className={`${sizes[size]} rounded-full bg-gradient-to-br ${gradients[medalha] || gradients.nenhuma} flex items-center justify-center shadow-lg border-2 border-white`}>
        <Medal className={`${size === 'lg' ? 'w-8 h-8' : size === 'md' ? 'w-6 h-6' : 'w-4 h-4'} text-white drop-shadow`} />
      </div>
    );
  };

  if (!child || !score) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <p className="font-inter text-gray-500">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-6">
        <div className="max-w-5xl mx-auto">
          <button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            className="mb-4 hover:bg-white/10 rounded-full p-2 transition-all"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <User className="w-7 h-7" strokeWidth={2.5} />
            <h1 className="font-nunito font-bold text-3xl">Perfil da Criança</h1>
            {child && child.numero > 0 && (
              <span className="bg-white/20 text-white text-sm font-bold px-3 py-1 rounded-full">#{child.numero}</span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header Card */}
        {(() => {
          const medal = getMedalInfo(score.medalha, score.total_pontos);
          return (
            <Card className={`rounded-2xl p-4 sm:p-6 mb-6 shadow-lg border-2 ${medal.bg}`}>
              {/* Photo + Actions row */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  {/* Photo */}
                  <div className="relative shrink-0">
                    {child.foto_url ? (
                      <img src={child.foto_url.startsWith('http') ? child.foto_url : `${BACKEND_URL}${child.foto_url}`} alt={child.nome}
                        className="w-20 h-20 rounded-2xl object-cover object-center aspect-square border-2 border-white shadow-md bg-white"
                        onError={(e) => { e.target.onerror = null; e.target.src = ''; e.target.className = 'hidden'; }}
                      />
                    ) : null}
                    <div className={`w-20 h-20 rounded-2xl bg-[#E1ECE5] items-center justify-center border-2 border-white shadow-sm ${child.foto_url ? 'hidden' : 'flex'}`}
                      id="photo-placeholder">
                      <User className="w-8 h-8 text-[#1A4D2E]/40" />
                    </div>
                    {canEdit() && (
                      <>
                        <input type="file" accept="image/*,.heic,.heif" id="photo-upload" className="hidden" onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          try {
                            const { compressImage } = await import('../lib/imageCompress');
                            const compressed = await compressImage(file);
                            if (compressed.size > 15 * 1024 * 1024) {
                              const sizeMB = (compressed.size / (1024 * 1024)).toFixed(1);
                              toast.error(`Foto muito grande (${sizeMB}MB). Maximo 15MB.`);
                              return;
                            }
                            const formData = new FormData();
                            formData.append('file', compressed);
                            const res = await axios.post(`${API}/children/upload-photo?child_id=${child.id}`, formData, {
                              headers: { ...getAdminHeaders(), 'Content-Type': 'multipart/form-data' },
                              timeout: 60000,
                            });
                            setChild(prev => ({ ...prev, foto_url: res.data.foto_url }));
                            toast.success('Foto atualizada!');
                          } catch (err) {
                            const detail = err.response?.data?.detail;
                            toast.error(detail || (err.code === 'ECONNABORTED' ? 'Tempo esgotado. Tente novamente.' : 'Erro ao enviar foto'));
                          }
                        }} />
                        <button onClick={() => document.getElementById('photo-upload')?.click()}
                          className="absolute -bottom-1 -right-1 bg-[#F4D35E] text-[#1A4D2E] rounded-full p-1.5 shadow-md hover:bg-[#e8c74a]"
                          data-testid="btn-upload-photo">
                          <Camera className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                  {/* Name + Age */}
                  <div className="flex-1 min-w-0">
                    <h2 className="font-nunito font-bold text-xl sm:text-2xl lg:text-3xl text-[#1A4D2E] mb-1 break-words leading-tight">
                      {child.nome}
                    </h2>
                    <p className="font-inter text-sm sm:text-base text-gray-600">
                      {child.idade} {child.idade === 1 ? 'ano' : 'anos'}
                    </p>
                  </div>
                </div>
                {/* Action buttons */}
                <div className="flex items-center gap-2 shrink-0 justify-end sm:justify-start">
                  {canEdit() && (
                    <button
                      data-testid="btn-edit-child"
                      onClick={() => navigate(`/cadastro?edit=${id}`)}
                      className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full p-2.5 sm:p-3 shadow-md active:scale-95 transition-all"
                      title="Editar dados"
                    >
                      <Edit className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  )}
                  {canEdit() && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid="btn-delete-child"
                          className="bg-red-500 hover:bg-red-600 text-white rounded-full p-2.5 sm:p-3 shadow-md active:scale-95 transition-all"
                          title="Excluir criança"
                        >
                          <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir crianca</AlertDialogTitle>
                          <AlertDialogDescription>
                            Tem certeza que deseja excluir <strong>{child.nome}</strong>? Todas as atividades e pontuacoes serao removidas permanentemente.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={handleDeleteChild}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  <MedalBadge medalha={score.medalha} />
                </div>
              </div>

              {/* Medal Progress */}
              <div className="bg-white/60 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-inter text-sm text-gray-600">Total de Sementes</p>
                    <p className="font-nunito font-bold text-4xl text-[#1A4D2E]">
                      {score.total_pontos}
                    </p>
                  </div>
                  <div className="text-right">
                    <p data-testid="medal-label" className={`font-nunito font-bold text-lg ${medal.color}`}>
                      {medal.label}
                    </p>
                    {medal.nextThreshold && (
                      <p className="font-inter text-xs text-gray-500">
                        Faltam {medal.nextThreshold - score.total_pontos} para {medal.key === 'nenhuma' ? 'Bronze' : medal.key === 'bronze' ? 'Prata' : 'Ouro'}
                      </p>
                    )}
                  </div>
                </div>
                {medal.nextThreshold && (
                  <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                    <div
                      data-testid="medal-progress-bar"
                      className={`h-2.5 rounded-full transition-all duration-700 ${
                        medal.key === 'nenhuma' ? 'bg-green-400' :
                        medal.key === 'bronze' ? 'bg-amber-500' : 'bg-gray-400'
                      }`}
                      style={{ width: `${medal.progress * 100}%` }}
                    />
                  </div>
                )}
                {!medal.nextThreshold && (
                  <p className="font-inter text-sm text-yellow-600 font-semibold text-center mt-1">Medalha maxima alcancada!</p>
                )}
              </div>

          {/* Responsáveis */}
          {(child.pai || child.mae) && (
            <div className="space-y-2 pt-4 border-t border-gray-200">
              {child.pai && (
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="font-inter text-gray-600">Pai: {child.pai}</span>
                  {child.celular_pai && (
                    <span className="text-gray-400">• {child.celular_pai}</span>
                  )}
                </div>
              )}
              {child.mae && (
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="font-inter text-gray-600">Mãe: {child.mae}</span>
                  {child.celular_mae && (
                    <span className="text-gray-400">• {child.celular_mae}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>
          );
        })()}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { icon: Home, label: 'Presenças', count: score.total_presencas, pts: settings ? score.total_presencas * settings.presenca : null },
            { icon: Clock, label: 'Pontualidade', count: score.total_pontualidade, pts: settings ? score.total_pontualidade * settings.pontualidade : null },
            { icon: BookOpen, label: 'Bíblia', count: score.total_biblia, pts: settings ? score.total_biblia * settings.biblia : null },
            { icon: Home, label: 'Visitas', count: score.total_visitas, pts: settings ? score.total_visitas * settings.visita : null },
            { icon: HandHeart, label: 'Devocionais', count: score.total_devocionais, pts: score.devocional_sementes ?? (settings ? score.total_devocionais * settings.devocional : null) },
            { icon: BookOpen, label: 'Versículos', count: score.total_versiculos, pts: score.versiculo_sementes ?? (settings ? score.total_versiculos * settings.versiculo : null) },
            { icon: Medal, label: 'Gincana', count: score.total_gincana, pts: score.total_gincana },
            { icon: TrendingUp, label: 'Se Vira', count: score.total_se_vira || 0, pts: score.total_se_vira || 0 },
          ].map((item, i) => (
            <Card key={i} className="bg-white rounded-xl p-4 shadow-md border border-[#E1ECE5]">
              <item.icon className="w-5 h-5 text-[#1A4D2E] mb-2" />
              <p className="font-nunito font-bold text-2xl text-[#1A4D2E]">{item.count}</p>
              <p className="font-inter text-xs text-gray-500">{item.label}</p>
              {item.pts !== null && item.pts !== undefined && (
                <p className="font-inter text-[10px] text-[#5A7A3B] font-semibold mt-1">{item.pts} sementes</p>
              )}
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 gap-4 mb-6">
          <button
            data-testid="btn-lancamento-semanal"
            onClick={() => navigate('/lancamento/semanal')}
            className="bg-[#F4D35E] hover:bg-[#D9B945] text-[#1A4D2E] rounded-2xl p-4 shadow-md active:scale-95 transition-all"
          >
            <Calendar className="w-6 h-6 mx-auto mb-2" />
            <p className="font-inter font-semibold text-sm">Lançar Atividade Semanal</p>
          </button>
        </div>

        {/* Compartilhar com Pais */}
        <Dialog>
          <DialogTrigger asChild>
            <button
              data-testid="btn-share-invite"
              onClick={fetchInviteLink}
              className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-2xl p-4 shadow-md active:scale-95 transition-all mb-6"
            >
              <div className="flex items-center justify-center gap-2">
                <Share2 className="w-5 h-5" />
                <span className="font-inter font-semibold">Compartilhar com Pais</span>
              </div>
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-nunito font-bold text-2xl text-[#1A4D2E]">
                Convite para Pais
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {inviteLink && (
                <>
                  {/* QR Code */}
                  <div className="bg-white p-6 rounded-xl border-2 border-gray-200 flex justify-center">
                    <QRCodeSVG 
                      value={inviteLink} 
                      size={200}
                      level="H"
                      includeMargin
                    />
                  </div>

                  <div className="text-center">
                    <p className="font-inter text-sm text-gray-600 mb-4">
                      Escaneie o QR Code ou copie o link abaixo:
                    </p>
                    
                    {/* Link */}
                    <div className="bg-gray-50 rounded-xl p-4 mb-4 break-all">
                      <p className="font-mono text-xs text-gray-700">{inviteLink}</p>
                    </div>

                    {/* Copy Button */}
                    <button
                      onClick={copyToClipboard}
                      className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full px-6 py-3 font-semibold shadow-md active:scale-95 transition-all flex items-center justify-center gap-2"
                    >
                      {copied ? (
                        <>
                          <Check className="w-5 h-5" />
                          Link Copiado!
                        </>
                      ) : (
                        <>
                          <Copy className="w-5 h-5" />
                          Copiar Link
                        </>
                      )}
                    </button>

                    {/* WhatsApp Button */}
                    {(child?.celular_pai || child?.celular_mae) && (
                      <button
                        data-testid="btn-send-whatsapp"
                        onClick={sendWhatsApp}
                        className="w-full mt-3 bg-[#25D366] hover:bg-[#1da851] text-white rounded-full px-6 py-3 font-semibold shadow-md active:scale-95 transition-all flex items-center justify-center gap-2"
                      >
                        <MessageCircle className="w-5 h-5" />
                        Enviar via WhatsApp
                      </button>
                    )}

                    <p className="font-inter text-xs text-gray-500 mt-4">
                      💡 Os pais poderão se cadastrar usando este link e acompanhar as atividades
                    </p>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* History Tabs */}
        <Tabs defaultValue="report" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="report" data-testid="tab-relatorio">Relatório</TabsTrigger>
            <TabsTrigger value="evolution" data-testid="tab-evolution">Evolução</TabsTrigger>
            <TabsTrigger value="weekly" data-testid="tab-semanal">Semanais</TabsTrigger>
          </TabsList>

          {/* Tab Relatório Detalhado */}
          <TabsContent value="report" className="space-y-4">
            {weeklyDetail && weeklyDetail.weeks?.length > 0 ? (
              <Card className="bg-white rounded-2xl shadow-md border border-[#E1ECE5] overflow-hidden">
                <div className="bg-[#1A4D2E] px-4 py-3">
                  <h3 className="font-nunito font-bold text-white text-sm flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" /> Pontuação por Semana
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600 border-b">
                        <th className="px-2 py-2 text-left font-semibold sticky left-0 bg-gray-50 z-10">Semana</th>
                        <th className="px-1.5 py-2 text-center font-semibold" title="Presença">Pre</th>
                        <th className="px-1.5 py-2 text-center font-semibold" title="Pontualidade">Pon</th>
                        <th className="px-1.5 py-2 text-center font-semibold" title="Bíblia">Bíb</th>
                        <th className="px-1.5 py-2 text-center font-semibold" title="Visita">Vis</th>
                        <th className="px-1.5 py-2 text-center font-semibold" title="Versículo">Ver</th>
                        <th className="px-1.5 py-2 text-center font-semibold" title="Devocional">Dev</th>
                        <th className="px-1.5 py-2 text-center font-semibold" title="Gincana">Gin</th>
                        <th className="px-1.5 py-2 text-center font-semibold" title="Se Vira">SV</th>
                        <th className="px-2 py-2 text-center font-bold text-[#1A4D2E]">Total</th>
                        <th className="px-2 py-2 text-center font-bold text-[#5A7A3B]">Acum.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {weeklyDetail.weeks.map((w, i) => (
                        <tr key={i} className={`border-b border-gray-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                          <td className="px-2 py-2 font-semibold text-gray-700 whitespace-nowrap sticky left-0 bg-inherit z-10">{w.label}</td>
                          <td className="px-1.5 py-2 text-center">{w.pts_presenca > 0 ? <span className="text-green-700 font-semibold">{w.pts_presenca}</span> : <span className="text-gray-300">-</span>}</td>
                          <td className="px-1.5 py-2 text-center">{w.pts_pontualidade > 0 ? <span className="text-blue-700 font-semibold">{w.pts_pontualidade}</span> : <span className="text-gray-300">-</span>}</td>
                          <td className="px-1.5 py-2 text-center">{w.pts_biblia > 0 ? <span className="text-purple-700 font-semibold">{w.pts_biblia}</span> : <span className="text-gray-300">-</span>}</td>
                          <td className="px-1.5 py-2 text-center">{w.pts_visita > 0 ? <span className="text-orange-700 font-semibold">{w.pts_visita}</span> : <span className="text-gray-300">-</span>}</td>
                          <td className="px-1.5 py-2 text-center">{w.pts_versiculo > 0 ? <span className="text-teal-700 font-semibold">{w.pts_versiculo}</span> : <span className="text-gray-300">-</span>}</td>
                          <td className="px-1.5 py-2 text-center">{w.pts_devocional > 0 ? <span className="text-emerald-700 font-semibold">{w.pts_devocional}</span> : <span className="text-gray-300">-</span>}</td>
                          <td className="px-1.5 py-2 text-center">{w.pts_gincana > 0 ? <span className="text-amber-700 font-semibold">{w.pts_gincana}</span> : <span className="text-gray-300">-</span>}</td>
                          <td className="px-1.5 py-2 text-center">{w.pts_se_vira > 0 ? <span className="text-pink-700 font-semibold">{w.pts_se_vira}</span> : <span className="text-gray-300">-</span>}</td>
                          <td className="px-2 py-2 text-center font-bold text-[#1A4D2E] bg-green-50/50">{w.total_semana}</td>
                          <td className="px-2 py-2 text-center font-bold text-[#5A7A3B] bg-[#F2F7F4]">{w.acumulado}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#1A4D2E] text-white font-bold text-xs">
                        <td className="px-2 py-2.5 sticky left-0 bg-[#1A4D2E] z-10">TOTAL</td>
                        <td className="px-1.5 py-2.5 text-center">{weeklyDetail.weeks.reduce((s, w) => s + w.pts_presenca, 0)}</td>
                        <td className="px-1.5 py-2.5 text-center">{weeklyDetail.weeks.reduce((s, w) => s + w.pts_pontualidade, 0)}</td>
                        <td className="px-1.5 py-2.5 text-center">{weeklyDetail.weeks.reduce((s, w) => s + w.pts_biblia, 0)}</td>
                        <td className="px-1.5 py-2.5 text-center">{weeklyDetail.weeks.reduce((s, w) => s + w.pts_visita, 0)}</td>
                        <td className="px-1.5 py-2.5 text-center">{weeklyDetail.weeks.reduce((s, w) => s + w.pts_versiculo, 0)}</td>
                        <td className="px-1.5 py-2.5 text-center">{weeklyDetail.weeks.reduce((s, w) => s + w.pts_devocional, 0)}</td>
                        <td className="px-1.5 py-2.5 text-center">{weeklyDetail.weeks.reduce((s, w) => s + w.pts_gincana, 0)}</td>
                        <td className="px-1.5 py-2.5 text-center">{weeklyDetail.weeks.reduce((s, w) => s + w.pts_se_vira, 0)}</td>
                        <td className="px-2 py-2.5 text-center text-[#8BC34A]">{weeklyDetail.total_geral}</td>
                        <td className="px-2 py-2.5 text-center text-[#8BC34A]">{weeklyDetail.total_geral}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                  <p className="font-inter text-[10px] text-gray-400">
                    Legenda: Pre={weeklyDetail.settings?.presenca}pt/evento | Pon={weeklyDetail.settings?.pontualidade}pt | Bíb={weeklyDetail.settings?.biblia}pt | Vis={weeklyDetail.settings?.visita}pt | Ver={weeklyDetail.settings?.versiculo}pt | Dev={weeklyDetail.settings?.devocional}pt/dia | Gin=pts diretos | SV=pts diretos
                  </p>
                </div>
              </Card>
            ) : (
              <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]">
                <BarChart3 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="font-inter text-gray-400">Nenhuma atividade registrada</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="evolution" className="space-y-4">
            <Card className="bg-white rounded-2xl p-6 shadow-md border border-[#E1ECE5]">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-[#1A4D2E]" />
                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E]">
                  Gráfico de Evolução
                </h3>
              </div>
              {evolutionData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={evolutionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E1ECE5" />
                    <XAxis 
                      dataKey="data" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        try {
                          const date = new Date(value + 'T12:00:00');
                          return date.toLocaleDateString('pt-BR', { month: 'short', day: 'numeric' });
                        } catch {
                          return value;
                        }
                      }}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E1ECE5',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                      labelFormatter={(value) => {
                        try {
                          const date = new Date(value + 'T12:00:00');
                          return date.toLocaleDateString('pt-BR');
                        } catch {
                          return value;
                        }
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="pontos_acumulados" 
                      stroke="#1A4D2E" 
                      strokeWidth={3}
                      name="Pontos Acumulados"
                      dot={{ fill: '#1A4D2E', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-gray-500 py-8">Sem dados suficientes para exibir o gráfico</p>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="weekly" className="space-y-4">
            {(() => {
              // Agrupar TUDO por semana (sábado) usando utilitário padronizado
              const allByWeek = {};

              // Lançamentos semanais (Admin)
              weeklyActivities.forEach((act, idx) => {
                const wk = getSaturdayOfWeek(act.semana || '');
                if (!allByWeek[wk]) allByWeek[wk] = { admin: [], pais: [], totalAdmin: 0, totalPais: 0 };
                allByWeek[wk].admin.push({ ...act, _idx: idx });
                allByWeek[wk].totalAdmin += (act.pontos_semana || 0);
              });

              // Lançamentos diários (Pais)
              dailyActivities.forEach(a => {
                if (!a.data) return;
                const wk = getSaturdayOfWeek(a.data);
                if (!allByWeek[wk]) allByWeek[wk] = { admin: [], pais: [], totalAdmin: 0, totalPais: 0 };
                allByWeek[wk].pais.push(a);
                allByWeek[wk].totalPais += (a.pontos_dia || 0);
              });

              const weeks = Object.keys(allByWeek).sort().reverse();

              if (weeks.length === 0) {
                return (
                  <Card className="bg-white rounded-xl p-8 text-center shadow-sm border border-[#E1ECE5]">
                    <p className="font-inter text-gray-500">Nenhuma atividade registrada</p>
                  </Card>
                );
              }

              return weeks.map(wk => {
                const group = allByWeek[wk];
                const weekNum = getWeekNumber(wk);
                const satDate = new Date(wk + 'T12:00:00');
                const sunDate = new Date(satDate);
                sunDate.setDate(sunDate.getDate() - 6);
                const totalSemana = group.totalAdmin + group.totalPais;

                return (
                  <Card key={wk} className="bg-white rounded-2xl shadow-md border border-[#E1ECE5] overflow-hidden">
                    {/* Cabeçalho da Semana */}
                    <div className="bg-[#1A4D2E] px-4 py-3 flex items-center justify-between">
                      <div>
                        <p className="font-nunito font-bold text-white text-sm">
                          Semana {weekNum}
                        </p>
                        <p className="font-inter text-[10px] text-white/60">
                          {sunDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} a {satDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-nunito font-extrabold text-[#8BC34A] text-lg">+{totalSemana}</p>
                        <p className="font-inter text-[10px] text-white/60">sementes</p>
                      </div>
                    </div>

                    <div className="divide-y divide-gray-100">
                      {/* Lançamentos do Admin */}
                      {group.admin.map((activity) => {
                        const idx = activity._idx;
                        const createdDate = activity.created_at ? new Date(activity.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
                        return (
                          <div key={`w-${idx}`} className="px-4 py-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full">ADMIN</span>
                                {createdDate && <span className="font-inter text-[10px] text-gray-400">{createdDate}</span>}
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="font-nunito font-bold text-sm text-[#1A4D2E]">+{activity.pontos_semana} pts</span>
                                {canEdit() && (
                                  <>
                                    <button onClick={() => openEditWeekly(activity)} className="text-[#1A4D2E] hover:bg-[#1A4D2E]/10 rounded-full p-1.5 transition-all" data-testid={`edit-weekly-${idx}`}>
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <button className="text-red-500 hover:bg-red-50 rounded-full p-1.5 transition-all" data-testid={`delete-weekly-${idx}`}>
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>Confirmar exclusao</AlertDialogTitle>
                                          <AlertDialogDescription>Tem certeza que deseja remover esta atividade?</AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                          <AlertDialogAction onClick={() => handleDeleteWeekly(activity.id)} className="bg-red-500 hover:bg-red-600">Remover</AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1.5 text-[11px]">
                              {activity.presenca && <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium">Presenca</span>}
                              {activity.pontualidade && <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">Pontualidade</span>}
                              {activity.biblia && <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full font-medium">Biblia</span>}
                              {activity.versiculo && <span className="bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full font-medium">Versiculo</span>}
                              {activity.visita > 0 && <span className="bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full font-medium">Visita: {activity.visita}</span>}
                              {activity.devocional > 0 && <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Devocional: +{activity.devocional}</span>}
                              {activity.se_vira > 0 && <span className="bg-pink-50 text-pink-700 px-2 py-0.5 rounded-full font-medium">Se Vira: +{activity.se_vira}</span>}
                              {activity.gincana > 0 && <span className="bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded-full font-medium">Gincana: +{activity.gincana}</span>}
                            </div>
                          </div>
                        );
                      })}

                      {/* Lançamentos dos Pais */}
                      {group.pais.map((a, i) => (
                        <div key={`d-${i}`} className="px-4 py-3 bg-emerald-50/30">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full">PAIS</span>
                              <span className="font-inter text-[10px] text-gray-400">
                                {new Date(a.data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', weekday: 'short' })}
                              </span>
                              {a.devocional && <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-[11px] font-medium">Devocional</span>}
                            </div>
                            <span className="font-nunito font-bold text-sm text-emerald-700">+{a.pontos_dia || 0} pts</span>
                          </div>
                          {a.evidencia && <p className="font-inter text-[10px] text-gray-500 mt-1 italic ml-1">{a.evidencia}</p>}
                        </div>
                      ))}

                      {/* Rodapé com totais */}
                      <div className="px-4 py-2.5 bg-gray-50 flex items-center justify-between">
                        <span className="font-inter text-[10px] text-gray-400">
                          {group.admin.length} lanc. admin{group.pais.length > 0 ? ` + ${group.pais.length} lanc. pais` : ''}
                        </span>
                        <span className="font-nunito font-bold text-xs text-[#1A4D2E]">
                          Total: {totalSemana} sementes
                        </span>
                      </div>
                    </div>
                  </Card>
                );
              });
            })()}

            {/* Edit Weekly Dialog */}
            <Dialog open={editWeeklyOpen} onOpenChange={setEditWeeklyOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-nunito font-bold text-xl text-[#1A4D2E]">Editar Atividade Semanal</DialogTitle>
                </DialogHeader>
                {editWeeklyData && (
                  <div className="space-y-4 py-2">
                    <div>
                      <Label className="font-inter font-semibold text-[#1A4D2E] mb-1 block">Semana</Label>
                      <Input
                        type="date"
                        data-testid="edit-weekly-semana"
                        value={editWeeklyData.semana}
                        onChange={(e) => setEditWeeklyData(d => ({ ...d, semana: e.target.value }))}
                        className="rounded-xl"
                      />
                    </div>
                    {[
                      { key: 'presenca', label: 'Presença', color: 'green' },
                      { key: 'pontualidade', label: 'Pontualidade', color: 'blue' },
                      { key: 'biblia', label: 'Biblia', color: 'purple' },
                      { key: 'visita', label: 'Visita', color: 'orange' },
                      { key: 'versiculo', label: 'Versículo', color: 'teal' },
                    ].map(item => (
                      <div key={item.key} className={`flex items-center justify-between p-3 bg-${item.color}-50 rounded-xl`}>
                        <Label className="font-inter font-semibold text-[#1A4D2E]">{item.label}</Label>
                        <Switch
                          data-testid={`edit-weekly-${item.key}`}
                          checked={editWeeklyData[item.key]}
                          onCheckedChange={(c) => setEditWeeklyData(d => ({ ...d, [item.key]: c }))}
                          className="data-[state=checked]:bg-[#1A4D2E]"
                        />
                      </div>
                    ))}
                    <div>
                      <Label className="font-inter font-semibold text-[#1A4D2E] mb-1 block">Pontos Gincana</Label>
                      <Input
                        type="number"
                        min="0"
                        data-testid="edit-weekly-gincana"
                        value={editWeeklyData.gincana}
                        onChange={(e) => setEditWeeklyData(d => ({ ...d, gincana: parseInt(e.target.value) || 0 }))}
                        className="rounded-xl"
                      />
                    </div>
                    <div>
                      <Label className="font-inter font-semibold text-[#1A4D2E] mb-1 block">Se Vira nos 5</Label>
                      <Input
                        type="number"
                        min="0"
                        data-testid="edit-weekly-se-vira"
                        value={editWeeklyData.se_vira}
                        onChange={(e) => setEditWeeklyData(d => ({ ...d, se_vira: parseInt(e.target.value) || 0 }))}
                        className="rounded-xl"
                      />
                    </div>
                    <Button
                      data-testid="save-edit-weekly"
                      onClick={handleEditWeekly}
                      disabled={editLoading}
                      className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-5"
                    >
                      {editLoading ? 'Salvando...' : 'Salvar Alteracoes'}
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>
      </div>

      <Navigation active="participantes" />
    </div>
  );
};

export default PerfilCrianca;
